import React, { useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AIWriteButton — one-click "Write it for me" that fills a form field with
 * GPT 5.6 Sol output. Drop it next to any textarea:
 *
 *   <AIWriteButton
 *     kind="product_description"
 *     prompt={form.name || "spicy chicken sandwich"}
 *     tone="punchy"
 *     maxWords={40}
 *     onText={(t) => setForm(f => ({...f, description: t}))}
 *   />
 *
 * Props:
 *   kind       — "vendor_bio" | "product_description" | "menu_tagline" | "shop_headline"
 *   prompt     — short seed context ("family taco truck", or the current form.name)
 *   tone       — friendly | punchy | premium | playful (default friendly)
 *   maxWords   — cap on output length (default 60)
 *   onText     — callback receiving the generated string
 *   className  — extra classes
 *   label      — override button text (default "Write it for me")
 *   testid     — data-testid
 */
export default function AIWriteButton({
  kind,
  prompt,
  tone = "friendly",
  maxWords = 60,
  onText,
  className = "",
  label = "Write it for me",
  testid,
}) {
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    const seed = (prompt || "").trim();
    if (!seed) {
      toast.error("Add a couple of words first — Hidden Gem AI needs a hint.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/ai/write", { kind, prompt: seed, tone, max_words: maxWords });
      onText?.(data.text);
      toast.success("AI-generated ✨");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI is thinking… please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      onClick={generate}
      disabled={loading}
      className={`h-8 px-3 rounded-full bg-gradient-to-r from-pink-500/20 to-cyan-500/20 hover:from-pink-500/30 hover:to-cyan-500/30 border border-pink-400/30 text-white text-[11px] font-black inline-flex items-center gap-1.5 ${className}`}
      data-testid={testid || `ai-write-btn-${kind}`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {loading ? "Writing…" : label}
    </Button>
  );
}
